/**
 * WorkspaceSidebar: the workspace panel (board "Sidebar"). No filter pills:
 * Favorites (only once something is a favorite), Shared with me (only when
 * something is), and one tree of the whole workspace. It replaced the legacy
 * workspace drawer in the same slot and keeps its width contract (reported upward through `usePanelResize` so the column,
 * not the panel, applies it) with its own mockup width, range and stored key.
 */

import { Box, Flex, Heading, Link, Text } from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router';

import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { ROUTES } from '../../../constants/routes';
import { usePanelResize } from '../../../hooks/ui/usePanelResize';
import { MaterialIcon } from '../../ui/MaterialIcon';
import {
  WORKSPACE_SIDEBAR_DEFAULT_WIDTH,
  WORKSPACE_SIDEBAR_MAX_WIDTH,
  WORKSPACE_SIDEBAR_MIN_WIDTH,
  WORKSPACE_SIDEBAR_WIDTH_KEY,
} from '../nav/navRailWidth';
import { PanelCollapseButton } from '../PanelHeader';
import { FavoritesSection } from './FavoritesSection';
import { FolderTree } from './FolderTree';
import { SharedWithMeSection } from './SharedWithMeSection';

interface WorkspaceSidebarProps {
  /** Reports the dragged width so the column can size itself and the page. */
  readonly onWidthChange?: (width: number) => void;
  readonly onDragStateChange?: (dragging: boolean) => void;
  /** Vacates the left-column slot (the header's collapse control). */
  readonly onClose: () => void;
}

export function WorkspaceSidebar({
  onWidthChange,
  onDragStateChange,
  onClose,
}: WorkspaceSidebarProps) {
  const { workspace } = useWorkspace();
  const { isDragging, handleProps } = usePanelResize({
    // Layout seeds the column from this same key and range, so the first
    // paint is already at this width.
    storageKey: WORKSPACE_SIDEBAR_WIDTH_KEY,
    minWidth: WORKSPACE_SIDEBAR_MIN_WIDTH,
    maxWidth: WORKSPACE_SIDEBAR_MAX_WIDTH,
    defaultWidth: WORKSPACE_SIDEBAR_DEFAULT_WIDTH,
    onDragStateChange,
    onWidthChange,
  });
  const name = workspace?.name ?? 'Workspace';

  return (
    <Flex
      direction="column"
      h="full"
      w="100%"
      position="relative"
      bg="sidebar.bg"
      data-testid="workspace-sidebar"
    >
      <Flex h="60px" flexShrink={0} align="center" gap={2} px={4}>
        <Heading
          as="h2"
          flex="1"
          minW={0}
          fontSize="18px"
          fontWeight="normal"
          lineHeight="1.3"
          isTruncated
          title={name}
        >
          {name}
        </Heading>
        <PanelCollapseButton label="Collapse workspace panel" onClick={onClose} />
      </Flex>
      <FavoritesSection />
      <SharedWithMeSection />
      <FolderTree />
      <SidebarFooter />
      {/* The drawer's resize band: 12px of hit area inside the panel, a 3px
          line drawn on hover, focus and drag. */}
      <Box
        {...handleProps}
        aria-label="Resize workspace panel"
        position="absolute"
        top={0}
        right="0"
        w="12px"
        h="100%"
        cursor="col-resize"
        zIndex={1}
        bg="transparent"
        sx={{
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '3px',
            height: '100%',
            background: isDragging ? 'var(--chakra-colors-border-focus)' : 'transparent',
            transition: 'background 120ms ease',
          },
          '&:hover::after, &:focus-visible::after': {
            background: 'var(--chakra-colors-border-focus)',
          },
          '&:focus-visible': { outline: 'none' },
        }}
      />
    </Flex>
  );
}

/**
 * The sidebar's footer (board "Sidebar"): one subtle Trash row under a
 * hairline, pinned below the tree. The Trash page counts as workspace context,
 * so the sidebar stays put when it is opened from here.
 */
function SidebarFooter() {
  return (
    <Box flexShrink={0} p={2} borderTop="1px solid" borderColor="border.subtle">
      <FooterRow to={ROUTES.trash} icon="delete" label="Trash" />
    </Box>
  );
}

/** One subtle footer row, marked current on its own page. */
function FooterRow({ to, icon, label }: { to: string; icon: string; label: string }) {
  const current = useLocation().pathname === to;
  return (
    <Link
      as={RouterLink}
      to={to}
      data-link-plain=""
      aria-current={current ? 'page' : undefined}
      display="flex"
      alignItems="center"
      gap={2}
      h="32px"
      px={2}
      borderRadius="md"
      fontSize="sm"
      textDecoration="none"
      color={current ? 'text.primary' : 'text.secondary'}
      bg={current ? 'bg.selection' : undefined}
      _hover={{ bg: current ? 'bg.selection' : 'bg.hover', textDecoration: 'none' }}
      _focusVisible={{ outline: 'none', boxShadow: 'outline' }}
    >
      <MaterialIcon name={icon} boxSize="18px" color="inherit" />
      <Text as="span">{label}</Text>
    </Link>
  );
}
